import { Canvas, useLoader, extend } from "@react-three/fiber";
import { CameraControls } from "@react-three/drei";
import { useErrorBoundary } from "use-error-boundary";
import React, { useEffect, useRef, useState } from "react";
import * as THREE from "three";

// Extend Three.js with the necessary components
extend({
  LineSegments: THREE.LineSegments,
  EdgesGeometry: THREE.EdgesGeometry,
  LineBasicMaterial: THREE.LineBasicMaterial
});

function ThreeDCanvas({
  currentRoom,
  showShutters,
  showRemovedShutters,
  showDrilling,
  showEdges,
  mode,
  selectionManager,
}) {
  const { ErrorBoundary, didCatch, error } = useErrorBoundary();

  function setLaminateProperties(
    panel,
    laminateTexture,
    identifier,
    imageHeight,
    imageWidth
  ) {
    laminateTexture = laminateTexture.clone();
    const laminateHeight = imageHeight || 2420;
    const laminateWidth = imageWidth || 1220;
    laminateTexture.wrapS = THREE.RepeatWrapping;
    laminateTexture.wrapT = THREE.RepeatWrapping;
    if (panel.rotated) {
      laminateTexture.rotation = (90 * Math.PI) / 180;
    }
    // FIXME: Side variable in conjunction with orientation should be used to calculate the texture repeat
    if (panel.orientation == "surface") {
      if (panel.axes?.["x"] == "length_1")
        laminateTexture.rotation = (90 * Math.PI) / 180;
      laminateTexture.repeat.set(
        panel.dimensions.width / laminateWidth,
        panel.dimensions.height / laminateHeight
      );
    }
    if (panel.orientation == "horizontal") {
      if (panel.axes?.["x"] == "length_1")
        laminateTexture.rotation = (90 * Math.PI) / 180;
      laminateTexture.repeat.set(
        panel.dimensions.width / laminateWidth,
        panel.dimensions.depth / laminateHeight
      );
    }
    if (panel.orientation == "vertical") {
      if (panel.axes?.["z"] == "length_1")
        laminateTexture.rotation = (90 * Math.PI) / 180;
      laminateTexture.repeat.set(
        panel.dimensions.depth / laminateWidth,
        panel.dimensions.height / laminateHeight
      );
    }
    return laminateTexture;
  }

  function LaminateMaterial(props) {
    const panel = props.panel;

    let frontLaminateTexture = panel.front_laminate
      ? useLoader(
          THREE.TextureLoader,
          `${panel.front_laminate.image_url}?3d=true`
        )
      : null;
    frontLaminateTexture = frontLaminateTexture
      ? setLaminateProperties(
          panel,
          frontLaminateTexture,
          "front",
          panel.front_laminate.image_height,
          panel.front_laminate.image_width
        )
      : null;
    let backLaminateTexture = panel.back_laminate
      ? useLoader(
          THREE.TextureLoader,
          `${panel.back_laminate.image_url}?3d=true`
        )
      : null;
    backLaminateTexture = backLaminateTexture
      ? setLaminateProperties(
          panel,
          backLaminateTexture,
          "back",
          panel.back_laminate.image_height,
          panel.back_laminate.image_width
        )
      : frontLaminateTexture;
    let topLaminateTexture = panel.top_laminate
      ? useLoader(
          THREE.TextureLoader,
          `${panel.top_laminate.image_url}?3d=true`
        )
      : null;
    topLaminateTexture = topLaminateTexture
      ? setLaminateProperties(
          panel,
          topLaminateTexture,
          "top",
          panel.top_laminate.image_height,
          panel.top_laminate.image_width
        )
      : frontLaminateTexture;
    let bottomLaminateTexture = panel.bottom_laminate
      ? useLoader(
          THREE.TextureLoader,
          `${panel.bottom_laminate.image_url}?3d=true`
        )
      : null;
    bottomLaminateTexture = bottomLaminateTexture
      ? setLaminateProperties(
          panel,
          bottomLaminateTexture,
          "bottom",
          panel.bottom_laminate.image_height,
          panel.bottom_laminate.image_width
        )
      : frontLaminateTexture;
    let leftLaminateTexture = panel.left_laminate
      ? useLoader(
          THREE.TextureLoader,
          `${panel.left_laminate.image_url}?3d=true`
        )
      : null;
    leftLaminateTexture = leftLaminateTexture
      ? setLaminateProperties(
          panel,
          leftLaminateTexture,
          "left",
          panel.left_laminate.image_height,
          panel.left_laminate.image_width
        )
      : frontLaminateTexture;
    let rightLaminateTexture = panel.right_laminate
      ? useLoader(
          THREE.TextureLoader,
          `${panel.right_laminate.image_url}?3d=true`
        )
      : null;
    rightLaminateTexture = rightLaminateTexture
      ? setLaminateProperties(
          panel,
          rightLaminateTexture,
          "right",
          panel.right_laminate.image_height,
          panel.right_laminate.image_width
        )
      : frontLaminateTexture;

    return [
      <meshStandardMaterial
        map={rightLaminateTexture}
        attach="material-0" //right
        roughness={0.4}
        metalness={0.2}
        attachArray="material"
        transparent={showDrilling}
        opacity={showDrilling ? 0.6 : 1}
      />,
      <meshStandardMaterial
        map={leftLaminateTexture}
        attach="material-1" //left
        roughness={0.4}
        metalness={0.2}
        attachArray="material"
        transparent={showDrilling}
        opacity={showDrilling ? 0.6 : 1}
      />,
      <meshStandardMaterial
        map={topLaminateTexture}
        attach="material-2" //top
        roughness={0.4}
        metalness={0.2}
        attachArray="material"
        transparent={showDrilling}
        opacity={showDrilling ? 0.6 : 1}
      />,
      <meshStandardMaterial
        map={bottomLaminateTexture}
        attach="material-3" //bottom
        roughness={0.4}
        metalness={0.2}
        attachArray="material"
        transparent={showDrilling}
        opacity={showDrilling ? 0.6 : 1}
      />,
      <meshStandardMaterial
        map={frontLaminateTexture}
        attach="material-4" //front
        roughness={0.4}
        metalness={0.2}
        attachArray="material"
        transparent={showDrilling}
        opacity={showDrilling ? 0.6 : 1}
      />,
      <meshStandardMaterial
        map={backLaminateTexture}
        attach="material-5" //back
        roughness={0.4}
        metalness={0.2}
        attachArray="material"
        transparent={showDrilling}
        opacity={showDrilling ? 0.6 : 1}
      />,
    ];
  }

  function getRotation(drilling) {
    let rotation = [0, 0, 0];

    if (drilling.side == "left") {
      rotation = [0, 0, Math.PI / 2];
    }
    if (drilling.side == "right") {
      rotation = [0, 0, -Math.PI / 2];
    }
    if (drilling.side == "front") {
      rotation = [Math.PI / 2, 0, 0];
    }
    if (drilling.side == "back") {
      rotation = [-Math.PI / 2, 0, 0];
    }
    if (drilling.side == "bottom") {
      rotation = [-Math.PI, 0, 0];
    }
    if (drilling.side == "top") {
      rotation = [0, 0, 0];
    }
    return rotation;
  }

  function getPosition(drilling, panel) {
    let position = drilling.position;
    if (drilling.type == "surface_hole") {
      if (drilling.side == "left") {
        position.x = 0;
      }
      if (drilling.side == "right") {
        position.x = panel.dimensions.width;
      }
      if (drilling.side == "front") {
        position.z = panel.dimensions.depth;
      }
      if (drilling.side == "back") {
        position.z = 0;
      }
      if (drilling.side == "bottom") {
        position.y = 0;
      }
      if (drilling.side == "top") {
        position.y = panel.dimensions.height;
      }
    }
    return position;
  }

  function getDrillingColor(type) {
    if (type == "dowel") {
      return "blue";
    }
    if (type == "minifix_rotor_screw") {
      return "red";
    }
    if (type == "minifix_housing_screw") {
      return "green";
    } else {
      return "yellow";
    }
  }

  function Panel(props) {
    const component = props.component;
    const panel = props.panel;
    const allDrillings = props.allDrillings;

    const meshRef = useRef();

    // Subscribe this component to the render-loop
    // useFrame((state, delta) => (meshRef.current.rotation.x += delta))

    const panelDrilling =
      showDrilling && allDrillings
        ? props.allDrillings[panel.true_uniq_id]
        : false;

    return (
      <>
        <mesh
          position={[
            panel.position.x +
              panel.dimensions.width / 2 +
              component.position.x,
            panel.position.y +
              panel.dimensions.height / 2 +
              component.position.y,
            panel.position.z +
              panel.dimensions.depth / 2 +
              component.position.z,
          ]}
          ref={meshRef}
          castShadow={true}
          receiveShadow={true}
          key={panel.uniq_id}
        >
          <boxGeometry
            args={[
              panel.dimensions.width,
              panel.dimensions.height,
              panel.dimensions.depth,
            ]}
          />
          <LaminateMaterial panel={panel} />
        </mesh>
        {showEdges && (
          <lineSegments
            position={[
              panel.position.x + panel.dimensions.width / 2 + component.position.x,
              panel.position.y + panel.dimensions.height / 2 + component.position.y,
              panel.position.z + panel.dimensions.depth / 2 + component.position.z,
            ]}
          >
            <edgesGeometry args={[
              new THREE.BoxGeometry(
                panel.dimensions.width,
                panel.dimensions.height,
                panel.dimensions.depth
              )
            ]} />
            <lineBasicMaterial color="black" linewidth={1} />
          </lineSegments>
        )}

        {showDrilling &&
          panelDrilling &&
          panelDrilling
            .filter((drilling) => drilling.type != "groove")
            .map((drilling, drillingIndex) => {
              return (
                //   FIXME: Position should contain height/width offset for drilling size depending on orientation
                <mesh
                  rotation={getRotation(drilling)}
                  position={[
                    panel.position.x +
                      component.position.x +
                      getPosition(drilling, panel).x,
                    panel.position.y +
                      component.position.y +
                      getPosition(drilling, panel).y,
                    panel.position.z +
                      component.position.z +
                      getPosition(drilling, panel).z,
                  ]}
                >
                  <cylinderGeometry
                    args={[
                      drilling.diameter / 2,
                      drilling.diameter / 2,
                      drilling.depth,
                    ]}
                  ></cylinderGeometry>
                  <meshStandardMaterial
                    color={getDrillingColor(drilling.fixture_type)}
                  />
                </mesh>
              );
            })}
      </>
    );
  }

  function AmbientLight({ intensity }) {
    return <ambientLight intensity={intensity} />;
  }

  function renderComponent(component, allDrillings) {
    return component.panels
      .filter((panel) => !panel.is_removed || showRemovedShutters)
      .map((panel, panelIndex) => {
        return (
          <Panel
            panel={panel}
            component={component}
            allDrillings={allDrillings}
          ></Panel>
        );
      });
  }

  function renderPartition(partition, allDrillings) {
    return renderComponent(partition.component, allDrillings);
  }

  function renderShutter(shutter, allDrillings) {
    return renderComponent(shutter.component, allDrillings);
  }

  function renderSpace(space, allDrillings) {
    if (!space) return;
    let panels = [];
    if (space.component) {
      let clonedComponent = structuredClone(space.component);
      clonedComponent.position.x += space.abs_position.x;
      clonedComponent.position.y += space.abs_position.y;
      clonedComponent.position.z += space.abs_position.z;
      panels.push(renderComponent(clonedComponent, allDrillings));
    }
    (space.children || []).map(
      (childSpace, spaceIndex) =>
        (panels = [...panels, ...renderSpace(childSpace, allDrillings)])
    );
    return panels;
  }

  function Module(props) {
    module = props.module;
    return (
      <group
        position={[
          module.position.x,
          module.position.y,
          module.position.z || 0,
        ]}
      >
        {renderComponent(module.template.base_component, module.drillings)}
        {module.template.partitions.map((partition, partitionIndex) =>
          renderPartition(partition, module.drillings)
        )}
        {renderSpace(module.template.root_space, module.drillings)}
        {showShutters &&
          module.template.shutters.map((shutter, shutterIndex) =>
            renderShutter(shutter, module.drillings)
          )}
      </group>
    );
  }

  let allModules = currentRoom.modules;
  currentRoom.groups.map((group) => {
    allModules = [...allModules, ...group.modules];
  });

  function Room() {
    if (mode === "canvas")
      return allModules.map((module, module_index) => (
        <Module key={module_index} module={module} />
      ));
    else {
      let ids = [];
      const activeModule = selectionManager.activeModule();
      const activeGroup =
        selectionManager.activeGroup() &&
        selectionManager.findGroup(selectionManager.activeGroup().id);

      if (activeGroup) ids = activeGroup.modules.map((e) => e.id);
      else if (activeModule) ids = [activeModule.id];

      return allModules
        .filter((module) => ids.includes(module.id))
        .map((module, module_index) => (
          <Module key={module_index} module={module} />
        ));
    }
  }

  let camera = new THREE.PerspectiveCamera(90, 1, 0.1, 1000000);

  const [savedCameraState, setSavedCameraState] = useState(null);

  function Scene() {
    const cameraControlsRef = useRef();

    useEffect(() => {
      if (savedCameraState) {
        // FIXME: This doesnt work for some reason
        cameraControlsRef.current.setLookAt(
          // Restore saved camera position and target
          savedCameraState.position.x,
          savedCameraState.position.y,
          savedCameraState.position.z,
          savedCameraState.target.x,
          savedCameraState.target.y,
          savedCameraState.target.z,
          false
        );
      } else {
        let module = null;

        if (mode === "canvas") module = allModules[0];
        else {
          const activeModule = selectionManager.activeModule();
          const activeGroup = selectionManager.activeGroup();

          if (activeModule) {
            if (activeModule.is_packed) module = activeGroup.modules[0];
            else module = activeModule;
          } else if (activeGroup) module = activeGroup.modules[0];
        }

        if (!module) return;

        const { position, dimensions } = module;

        cameraControlsRef.current.setLookAt(
          position.x + dimensions.width / 2,
          position.y + dimensions.height / 2,
          (position.z || 0) + dimensions.depth + 3000,
          position.x + dimensions.width / 2,
          position.y + dimensions.height / 2,
          0,
          false
        );
      }

      return () => {
        const currentPos = camera.position;
        if (cameraControlsRef.current && currentPos) {
          const target = cameraControlsRef.current.getTarget();
          setSavedCameraState({
            position: { x: currentPos.x, y: currentPos.y, z: currentPos.z },
            target: { x: target.x, y: target.y, z: target.z },
          });
        }
      };
    }, [camera, allModules]);

    return (
      <>
        <CameraControls
          ref={cameraControlsRef}
          minDistance={0.1}
          enabled={true}
          verticalDragToForward={false}
          dollyToCursor={true}
          infinityDolly={true}
        />
        {AmbientLight({ intensity: Math.PI / 2 })}
        <Room />
        <pointLight
          position={[2000, 200, 1600]}
          shadows={true}
          decay={0}
          intensity={Math.PI}
        />
      </>
    );
  }

  return didCatch ? (
    <div>{error.message}</div>
  ) : (
    <div className={"fh flex-grow-1 position-relative"}>
      <ErrorBoundary>
        <Canvas camera={camera} fallback={<div>Sorry no WebGL supported!</div>}>
          <Scene></Scene>
        </Canvas>
      </ErrorBoundary>
    </div>
  );
}

export default ThreeDCanvas;
